"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { ArrowLeft, Check, Globe, Menu, Search, User, X } from "lucide-react";
import {
  EJTECH_FINANCING_ROUTE,
  EJTECH_SEARCH_ROUTE,
  ROUTES,
  ejTechCategoryRoute,
} from "@/lib/routes";
import { Link, usePathname, useRouter } from "@/i18n/routing";
import { EJTECH_COLORS, EJTECH_FONT, EJTECH_TYPE } from "@/lib/theme/ej-tech";

type NavLink = { label: string; href: string; isHome?: boolean };

// Chinese-only language options (English removed per product decision).
const LOCALES: { value: "zh-TW" | "zh-CN"; label: string; short: string }[] = [
  { value: "zh-TW", label: "繁體中文", short: "繁體" },
  { value: "zh-CN", label: "简体中文", short: "简体" },
];

/*
  Every entry now opens its OWN listing.

  These all pointed at `/ej-tech/category?type=<x>` — a single page hardcoded to
  the title "CEO_AI" and one fixed article list, with the `type` param never
  read. Six labels, two of them sharing a `type` value outright, opened the same
  page showing the same stories. QA: "these are all category listing page, they
  should have the same layout and functions", and "change type param to slug
  instead".

  HK Startups is NOT a category listing — it is the funding table. QA: "HK
  Startups should redirect to /ej-tech/financing-record".
*/
const NAV_LINKS: NavLink[] = [
  { label: "首頁", href: "/ej-tech", isHome: true },
  { label: "CEO_AI", href: ejTechCategoryRoute("ceo-ai") },
  { label: "科技動態", href: ejTechCategoryRoute("tech-pulse") },
  { label: "HK Startups", href: EJTECH_FINANCING_ROUTE },
  { label: "人物專訪", href: ejTechCategoryRoute("interviews") },
  { label: "專家分析", href: ejTechCategoryRoute("experts") },
  { label: "創新生態圈", href: ejTechCategoryRoute("ecosystem") },
  { label: "活動盛事", href: ejTechCategoryRoute("events") },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const params = useParams<{ locale?: string }>();
  const pathname = usePathname();
  const router = useRouter();
  const currentLocale = (params?.locale ?? "zh-TW") as string;

  /*
    Which entry is current is a fact about the URL. `isHome` was a hardcoded
    flag on ONE fixture row, so 首頁 was painted primary and underlined on every
    page of the vertical and no other entry was ever marked. QA: "the nav item
    active style should only apply when the current route matches the route of
    the nav item".

    Compared on the PATH only — `usePathname` drops the locale prefix but keeps
    any query string, and the home entry must not match every sub-page.
  */
  const currentPath = (pathname ?? "").split("?")[0];
  const isCurrent = (href: string) => href.split("?")[0] === currentPath;
  const chooseLocale = (locale: "zh-TW" | "zh-CN") => {
    if (locale === currentLocale) return;
    router.replace(pathname, { locale });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const headerStyle = useMemo<React.CSSProperties>(
    () => ({
      backgroundColor: scrolled
        ? "rgba(255,255,255,0.86)"
        : EJTECH_COLORS.surface,
      backdropFilter: scrolled ? "saturate(160%) blur(12px)" : undefined,
      WebkitBackdropFilter: scrolled ? "saturate(160%) blur(12px)" : undefined,
      boxShadow: scrolled
        ? "0 1px 0 rgba(11,18,32,0.08), 0 8px 24px -16px rgba(11,18,32,0.18)"
        : `inset 0 -1px 0 ${EJTECH_COLORS.hairline}`,
      transition: "background-color .25s ease, box-shadow .25s ease",
      fontFamily: EJTECH_FONT.body,
      color: EJTECH_COLORS.ink,
    }),
    [scrolled],
  );

  return (
    <header className="sticky top-0 z-50 w-full" style={headerStyle}>
      {/*
        Back to HKEJ.

        The masthead had no way out of the vertical — QA: "missing back to HKEJ
        navigation on navbar". Same utility strip as EJ Education's, in EJ
        Tech's own surface and rule, so the two verticals answer the same way.

        A PLAIN anchor to an absolute `/zh-TW`, for the reasons written up on
        the EJ Education one: it drops out of the client router entirely, and
        the destination is the main site, which is Traditional Chinese, so a
        Simplified reader lands there too. `flex h-full` rather than
        `inline-flex` makes the whole 32px strip beside the mark clickable
        instead of only the 18px logo's own box.
      */}
      <div
        style={{
          backgroundColor: EJTECH_COLORS.surfaceAlt,
          borderBottom: `1px solid ${EJTECH_COLORS.hairline}`,
        }}
      >
        <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-8 items-center">
            <a
              href="/zh-TW"
              aria-label="回到信報財經新聞"
              className="group flex h-full w-fit items-center gap-1.5 pr-2 text-[color:var(--rest-fg)] transition-colors hover:text-[color:var(--hover-fg)] motion-reduce:transition-none"
              style={
                {
                  ["--rest-fg" as string]: EJTECH_COLORS.muted,
                  ["--hover-fg" as string]: EJTECH_COLORS.primary,
                } as React.CSSProperties
              }
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5 motion-reduce:transition-none" />
              <Image
                src="/images/nav-v2/hkej-back.png"
                alt="信報 www.hkej.com"
                width={133}
                height={23}
                className="h-[16px] w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1280px] items-center gap-6 px-4 py-3 sm:px-6 lg:gap-8 lg:px-8 lg:py-4">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="-ml-2 inline-flex h-10 w-10 items-center justify-center lg:hidden"
        >
          <Menu className="h-6 w-6" style={{ color: EJTECH_COLORS.ink }} />
        </button>

        {/* Logo */}
        <Link
          href="/ej-tech"
          aria-label="EJ Tech"
          className="inline-flex shrink-0 items-center"
        >
          <Image
            src="/images/ej-tech/ej-tech-logo.png"
            alt="EJ Tech"
            width={156}
            height={40}
            className="h-9 w-auto sm:h-10"
            priority
            unoptimized
          />
        </Link>

        {/* Desktop nav */}
        <nav
          aria-label="Primary"
          className="hidden flex-1 lg:flex lg:justify-center"
        >
          <ul className="flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const active = isCurrent(link.href);
              return (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    aria-current={active ? "page" : undefined}
                    className="group relative inline-flex items-center px-3 py-2 transition-colors"
                    style={{
                      ...EJTECH_TYPE.navLink,
                      color: active
                        ? EJTECH_COLORS.primary
                        : EJTECH_COLORS.ink,
                    }}
                  >
                    <span>{link.label}</span>
                    <span
                      aria-hidden
                      className={`absolute inset-x-3 -bottom-[3px] h-[3px] origin-left rounded-full transition-transform duration-300 group-hover:scale-x-100 ${
                        active ? "scale-x-100" : "scale-x-0"
                      }`}
                      style={{
                        backgroundColor: active
                          ? EJTECH_COLORS.primary
                          : EJTECH_COLORS.accent,
                      }}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-2 lg:ml-0 lg:gap-3">
          {/* Bare <button>s with no handler: both icons looked live and did
              nothing. A control that advertises itself and does nothing is the
              defect QA filed against lj's copy; these are the same components
              in the other verticals. */}
          {/* The vertical's OWN search, not the main site's — QA: "missing
              search page". */}
          <Link
            href={EJTECH_SEARCH_ROUTE}
            aria-label="Search"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
          >
            <Search className="h-5 w-5" style={{ color: EJTECH_COLORS.ink }} />
          </Link>
          <LocaleMenu
            currentLocale={currentLocale}
            onChoose={chooseLocale}
          />
          {/* Visible on a phone too. It was `hidden … sm:inline-flex`, so below
              640px the masthead offered search, language and 訂閱 but no way to
              reach the account — and the drawer does not carry one either. */}
          <Link
            href={ROUTES.profile}
            aria-label="Account"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
          >
            <User className="h-5 w-5" style={{ color: EJTECH_COLORS.ink }} />
          </Link>
          {/* Was a handler-less <button>: the most prominent control in the
              masthead advertised itself and did nothing. QA: "click on
              subscribe button should redirect to /subscription". The hover was
              also two JS handlers writing `style`, which skipped keyboard focus
              and prefers-reduced-motion. */}
          <Link
            href={ROUTES.subscription}
            className="group ml-1 inline-flex items-center rounded-full bg-[color:var(--rest-bg)] px-4 py-2 shadow-[0_0_0_0_var(--ring)] transition-all duration-200 hover:bg-[color:var(--hover-bg)] hover:shadow-[0_0_0_3px_var(--ring)] motion-reduce:transition-none"
            style={
              {
                ...EJTECH_TYPE.navLink,
                fontSize: 13,
                fontWeight: 700,
                color: EJTECH_COLORS.textOnDark,
                ["--rest-bg" as string]: EJTECH_COLORS.primary,
                ["--hover-bg" as string]: EJTECH_COLORS.primaryDark,
                ["--ring" as string]: EJTECH_COLORS.accentSoft,
              } as React.CSSProperties
            }
          >
            訂閱
          </Link>
        </div>
      </div>

      {/* Mobile drawer — slides in from the left. Kept mounted so it can
          animate both in and out; clicks pass through when closed. */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          mobileOpen ? "" : "pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          aria-label="Close menu"
          className="absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out"
          style={{ opacity: mobileOpen ? 1 : 0 }}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className="absolute left-0 top-0 flex h-full w-[88%] max-w-[380px] flex-col overflow-y-auto p-6"
          style={{
            backgroundColor: EJTECH_COLORS.surface,
            fontFamily: EJTECH_FONT.body,
            color: EJTECH_COLORS.ink,
            transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.3s ease-out",
            boxShadow: mobileOpen ? "0 0 40px rgba(0,0,0,0.25)" : "none",
          }}
        >
            <div className="mb-6 flex items-center justify-between">
              <Image
                src="/images/ej-tech/ej-tech-logo.png"
                alt="EJ Tech"
                width={120}
                height={32}
                className="h-8 w-auto"
                unoptimized
              />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full"
                style={{ border: `1px solid ${EJTECH_COLORS.hairline}` }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* No search field here. It was an `<input>` inside a bare
                `<label>` — no form, no handler — so typing a query and pressing
                Enter did nothing, which is the dead-control defect QA has filed
                against this product three times. The masthead's search icon is
                visible at every width and opens the vertical's own search page,
                so removing it costs the reader nothing. */}

            <ul className="mb-8 flex flex-col">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 transition-colors"
                    style={{
                      fontFamily: EJTECH_FONT.display,
                      fontSize: 22,
                      fontWeight: 700,
                      color: link.isHome
                        ? EJTECH_COLORS.primary
                        : EJTECH_COLORS.ink,
                      borderBottom: `1px solid ${EJTECH_COLORS.hairlineSoft}`,
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-auto flex flex-col gap-5 pt-6">
              <div
                className="flex items-center gap-3 pb-5"
                style={{ borderBottom: `1px solid ${EJTECH_COLORS.hairline}` }}
              >
                {LOCALES.map((loc, idx) => {
                  const active = loc.value === currentLocale;
                  return (
                    <button
                      key={loc.value}
                      type="button"
                      onClick={() => {
                        chooseLocale(loc.value);
                        setMobileOpen(false);
                      }}
                      style={{
                        ...EJTECH_TYPE.meta,
                        fontWeight: active ? 700 : 500,
                        color: active
                          ? EJTECH_COLORS.primary
                          : EJTECH_COLORS.muted,
                        borderRight:
                          idx < LOCALES.length - 1
                            ? `1px solid ${EJTECH_COLORS.hairline}`
                            : undefined,
                        paddingRight: idx < LOCALES.length - 1 ? 12 : 0,
                      }}
                    >
                      {loc.short}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full px-5 py-3"
                style={{
                  ...EJTECH_TYPE.navLink,
                  fontSize: 14,
                  fontWeight: 700,
                  color: EJTECH_COLORS.textOnDark,
                  backgroundColor: EJTECH_COLORS.primary,
                }}
              >
                訂閱
              </button>
            </div>
        </div>
      </div>
    </header>
  );
}

function LocaleMenu({
  currentLocale,
  onChoose,
}: {
  currentLocale: string;
  onChoose: (locale: "zh-TW" | "zh-CN") => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    /*
      Opens on HOVER as well as click — QA: "hover on the locale icon, should
      open the locale menu". The panel below starts flush against the trigger
      and carries its 8px offset as its own transparent padding, so the pointer
      never crosses unhovered ground on the way to the first row; a real gap
      there closes the menu before it can be reached, which is the defect filed
      against the sibling ej-education menu.
    */
    <div
      ref={containerRef}
      className="relative hidden lg:inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="切換語言"
        aria-expanded={open}
        title="繁體中文 / 简体中文"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
      >
        <Globe className="h-5 w-5" style={{ color: EJTECH_COLORS.ink }} />
      </button>
      {open && (
        <div className="absolute right-0 z-[70] pt-2" style={{ top: "100%", minWidth: 168 }}>
        <ul
          role="menu"
          aria-label="切換語言"
          style={{
            backgroundColor: EJTECH_COLORS.surface,
            border: `1px solid ${EJTECH_COLORS.hairline}`,
            borderRadius: 12,
            boxShadow: "0 16px 32px rgba(11,18,32,0.14)",
            padding: 4,
          }}
        >
          {LOCALES.map((loc) => {
            const active = loc.value === currentLocale;
            return (
              <li key={loc.value}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    onChoose(loc.value);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between rounded-lg transition-colors"
                  style={{
                    ...EJTECH_TYPE.navLink,
                    fontSize: 13,
                    padding: "8px 12px",
                    color: active ? EJTECH_COLORS.primary : EJTECH_COLORS.ink,
                    fontWeight: active ? 700 : 500,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      EJTECH_COLORS.surfaceAlt;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span>{loc.label}</span>
                  {active && <Check className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
        </div>
      )}
    </div>
  );
}
